/**
 * Ключи кеша TanStack Query для данных рабочего пространства и очистка кеша
 * при смене пользователя.
 *
 * Ключ всегда начинается с компании: одинаковый числовой ID объекта в двух
 * компаниях не может совпасть в кеше. Фильтры нормализуются, чтобы порядок
 * полей и пустые значения не порождали лишние запросы.
 */

import type { QueryClient } from '@tanstack/react-query';
import { sessionIdentity, useSessionStore } from '@/stores/session';

type Filter = Record<string, unknown>;

function isEmpty(value: unknown): boolean {
	return value === undefined || value === null || value === '';
}

function normalize(value: unknown): unknown {
	if (Array.isArray(value)) {
		const items = value.map(normalize);
		/* Порядок значений фильтра не меняет результат: ['a','b'] и ['b','a'] — один запрос */
		return items.every((item) => typeof item === 'string' || typeof item === 'number')
			? [...items].sort()
			: items;
	}
	if (value !== null && typeof value === 'object') {
		const source = value as Filter;
		return Object.fromEntries(
			Object.keys(source)
				.filter((key) => !isEmpty(source[key]))
				.sort()
				.map((key) => [key, normalize(source[key])]),
		);
	}
	return value;
}

/** Ключ кеша: компания, предмет запроса и нормализованные параметры */
export function workspaceKey(
	companyId: string,
	subject: string,
	params: Filter = {},
): readonly unknown[] {
	return ['workspace', companyId, subject, normalize(params)];
}

/**
 * Очищает кеш, когда прежний пользователь выходит или вместо него входит другой.
 * Повторный вход того же пользователя кеш сохраняет. Возвращает отписку.
 */
export function bindCacheToSessionIdentity(queryClient: QueryClient): () => void {
	let current = sessionIdentity(useSessionStore.getState().user);
	return useSessionStore.subscribe((state) => {
		const next = sessionIdentity(state.user);
		if (next === current) return;
		const previous = current;
		current = next;
		if (previous !== null) {
			queryClient.clear();
		}
	});
}
